import { useState } from 'react';
import { Card, Typography, Checkbox, Button, Tag, message } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

/**
 * Full-screen consent prompt, rendered by ProtectedRoute instead of the app
 * while the user has unanswered consents (new ones, or ones whose wording
 * changed). Required consents must be ticked to continue; optional ones are
 * recorded as accepted or declined either way.
 */
function ConsentScreen({ consents, onSubmit }) {
    const { logout } = useAuth();
    const [checked, setChecked] = useState({}); // consent id -> bool
    const [saving, setSaving] = useState(false);

    const missingRequired = consents.some(c => c.required && !checked[c.id]);

    const handleContinue = async () => {
        setSaving(true);
        try {
            await onSubmit(consents.map(c => ({ consent_id: c.id, version: c.version, accepted: !!checked[c.id] })));
        } catch (err) {
            message.error(err.status === 409
                ? 'Something was just updated — please review it again.'
                : (err.message || 'Could not save your choices'));
            setSaving(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minHeight: '100vh',
            padding: 'var(--sp-8) var(--sp-4)',
            backgroundColor: 'var(--bg-primary)',
        }}>
            <Card style={{ maxWidth: 640, width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: 'var(--sp-4)' }}>
                    <SafetyCertificateOutlined style={{ fontSize: 40, color: 'var(--ant-color-primary)' }} />
                    <Title level={3} style={{ marginBottom: 4 }}>Before you continue</Title>
                    <Text type="secondary">Please review and respond to the following.</Text>
                </div>

                {consents.map(c => (
                    <div key={c.id} style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: 8,
                        padding: 'var(--sp-3) var(--sp-4)',
                        marginBottom: 'var(--sp-3)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Text strong>{c.title}</Text>
                            {c.required ? <Tag color="red">Required</Tag> : <Tag>Optional</Tag>}
                        </div>
                        <Paragraph
                            type="secondary"
                            style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}
                        >
                            {c.body}
                        </Paragraph>
                        <Checkbox
                            checked={!!checked[c.id]}
                            onChange={e => setChecked(prev => ({ ...prev, [c.id]: e.target.checked }))}
                        >
                            {c.required ? 'I agree' : 'Yes, I agree (optional)'}
                        </Checkbox>
                    </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--sp-4)' }}>
                    <Button type="link" onClick={logout} style={{ paddingInline: 0 }}>Log out</Button>
                    <Button type="primary" size="large" loading={saving} disabled={missingRequired} onClick={handleContinue}>
                        Continue
                    </Button>
                </div>
                {missingRequired && (
                    <Text type="secondary" style={{ display: 'block', textAlign: 'right', fontSize: 12, marginTop: 6 }}>
                        Tick every required item to continue.
                    </Text>
                )}
            </Card>
        </div>
    );
}

export default ConsentScreen;
